import type { DigitalGroupDto, DigitalItemDto } from '@/types/dto';
import { Card } from '@/components/ui/card';
import { Tag } from '@/components/common/Tag';
import { ScoreTag } from '@/components/common/ScoreTag';

function fmtDate(iso: string) {
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function DigitalGroup({ group }: { group: DigitalGroupDto }) {
  return (
    <Card>
      <div className="flex items-baseline gap-2 mb-4 flex-wrap">
        <h2 className="text-2xl font-semibold">{group.label}</h2>
        <span className="text-muted text-sm">({group.items.length})</span>
      </div>

      {/* Desktop table */}
      <table className="hidden md:table w-full text-sm">
        <thead>
          <tr className="border-b-2 border-accent">
            <th style={{ width: 130 }} className="py-2 px-3 text-left text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">Entity</th>
            <th style={{ width: 100 }} className="py-2 px-3 text-left text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">When</th>
            <th className="py-2 px-3 text-left text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">Headline + Why</th>
            <th style={{ width: 60 }} className="py-2 px-3 text-center text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">Score</th>
          </tr>
        </thead>
        <tbody>
          {group.items.map((i) => <RowDesktop key={i.id} item={i} />)}
        </tbody>
      </table>

      {/* Mobile cards */}
      <ul className="md:hidden flex flex-col divide-y divide-border">
        {group.items.map((i) => <RowMobile key={i.id} item={i} />)}
      </ul>
    </Card>
  );
}

function RowDesktop({ item: i }: { item: DigitalItemDto }) {
  return (
    <tr className="border-b border-border align-top hover:bg-surface-hover">
      <td className="py-3 px-3">{i.entityName ?? '—'}</td>
      <td className="py-3 px-3 text-muted text-xs">{fmtDate(i.publishedAt)}</td>
      <td className="py-3 px-3">
        <a href={i.sourceUrl} target="_blank" rel="noopener" className="text-accent hover:underline">
          {i.headline}
        </a>
        {i.whyItMatters && (
          <div className="text-[0.85rem] mt-1 text-accent">↳ {i.whyItMatters}</div>
        )}
        {i.themes.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {i.themes.map((t) => (
              <Tag key={t} variant="neutral" className="text-[0.65rem]">{t}</Tag>
            ))}
          </div>
        )}
      </td>
      <td className="py-3 px-3 text-center align-middle">
        <ScoreTag score={i.score} />
      </td>
    </tr>
  );
}

function RowMobile({ item: i }: { item: DigitalItemDto }) {
  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[0.7rem] text-muted font-(family-name:--font-ui) uppercase tracking-wider mb-1">
            {i.entityName ?? '—'} · {fmtDate(i.publishedAt)}
          </div>
          <a href={i.sourceUrl} target="_blank" rel="noopener" className="text-accent hover:underline font-medium">
            {i.headline}
          </a>
          {i.whyItMatters && (
            <p className="text-sm mt-1 text-accent">↳ {i.whyItMatters}</p>
          )}
          {i.themes.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {i.themes.map((t) => (
                <Tag key={t} variant="neutral" className="text-[0.6rem]">{t}</Tag>
              ))}
            </div>
          )}
        </div>
        <ScoreTag score={i.score} />
      </div>
    </li>
  );
}
